import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import PublicHeader from '../components/PublicHeader';
import { PublicFooter } from '../components/SEOLandingTemplate';
import FloatingWhatsapp from '../components/FloatingWhatsapp';
import RequestQuoteDialog from '../components/RequestQuoteDialog';
import { Button } from '../components/ui/button';
import { PRODUCT_CATEGORIES, INDUSTRIES, SERVICES, LOCATIONS, COMPANY, organizationJsonLd } from '../content/seo';

const LIST_TITLES = {
  products: { title: 'Product Categories', desc: `Explore ${PRODUCT_CATEGORIES.length} categories of fresh and dry goods supplied across Rwanda.` },
  industries: { title: 'Industries We Serve', desc: `Trusted food supply for ${INDUSTRIES.length} industries — hotels, restaurants, supermarkets, schools and more.` },
  services: { title: 'Our Services', desc: 'Bulk supply, daily delivery, procurement support and custom orders.' },
  locations: { title: 'Delivery Locations', desc: 'Same-day and scheduled deliveries across Rwanda.' },
};

const DATA_MAP = {
  products: { list: PRODUCT_CATEGORIES, prefix: '/products' },
  industries: { list: INDUSTRIES, prefix: '/industries' },
  services: { list: SERVICES, prefix: '/services' },
  locations: { list: LOCATIONS, prefix: '/location' },
};

export default function SEOListPage({ kind }) {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const meta = LIST_TITLES[kind];
  const { list, prefix } = DATA_MAP[kind];
  const path = `/${kind}`;

  const collectionJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: meta.title,
    description: meta.desc,
    url: `${COMPANY.site}${path}`,
    hasPart: list.map((e) => ({
      '@type': 'WebPage',
      name: e.name,
      url: `${COMPANY.site}${prefix}/${e.slug}`,
      description: e.hero,
    })),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={meta.title}
        description={meta.desc}
        path={path}
        jsonLd={[collectionJsonLd, organizationJsonLd()]}
      />
      <PublicHeader onQuote={() => setQuoteOpen(true)} />

      <section className="max-w-6xl mx-auto px-4 py-14 text-center">
        <h1 className="text-4xl sm:text-5xl font-serif font-bold text-neutral-900">{meta.title}</h1>
        <p className="text-lg text-neutral-600 mt-3 max-w-2xl mx-auto">{meta.desc}</p>
      </section>

      <section className="max-w-6xl mx-auto px-4 pb-16">
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
          {list.map((e) => (
            <Link
              key={e.slug}
              to={`${prefix}/${e.slug}`}
              className="group block p-6 rounded-xl border border-neutral-200 hover:border-emerald-700 hover:shadow-md transition-all"
              data-testid={`seo-list-item-${e.slug}`}
            >
              <h2 className="text-xl font-serif font-bold text-neutral-900 group-hover:text-emerald-700">{e.name}</h2>
              <p className="text-sm text-neutral-600 mt-2 line-clamp-2">{e.hero}</p>
              <span className="inline-flex items-center gap-1 mt-4 text-emerald-700 font-semibold text-sm">
                Learn more <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-emerald-900 text-white">
        <div className="max-w-6xl mx-auto px-4 py-14 text-center">
          <h2 className="text-3xl font-serif font-bold">Ready to place an order?</h2>
          <p className="text-emerald-100 mt-2">
            Get a personal quote from our sales team.
          </p>
          <div className="mt-6 flex justify-center gap-3 flex-wrap">
            <Button size="lg" onClick={() => setQuoteOpen(true)} className="bg-amber-400 text-emerald-900 hover:bg-amber-300">
              Request Quote
            </Button>
            <a href={`tel:${COMPANY.phone}`}>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                Call sales
              </Button>
            </a>
          </div>
        </div>
      </section>

      <PublicFooter />
      <FloatingWhatsapp />
      <RequestQuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
    </div>
  );
}
